import { useMemo } from 'react';

import {
  DEFAULT_REMOVE_ITEMS,
  OPTIMIZED_DIFFICULTY_LEVEL_ORDER,
  OPTIMIZED_DURATION_ORDER,
  SEARCH_FILTER_COST_ORDER,
  SEARCH_FILTER_LEARNING_PRODUCT_ORDER,
  SEARCH_FILTER_LEARNING_PRODUCT_ORDER_EXPERIMENTAL,
} from 'bundles/search-common/constants/filter';
import { isInFilterTestVariant } from 'bundles/search-common/utils/experimentUtils';
import { getFilterItems } from 'bundles/search-common/utils/getFilterItemsTranslations';

/**
 * Configuration options for filter sorting
 */
export type FilterSortConfig = {
  /** Sort items by count (descending) instead of alphabetically */
  sortByCount?: boolean;
  /** Items to exclude from the sorted list */
  removeFilterItems?: string[];
  /** Enrich items with supportText and dialogText metadata (default: false) */
  enrichWithSupportText?: boolean;
  /** Disable custom order overrides (e.g., difficulty level, duration) to allow pure count/alphabetical sorting */
  disableCustomOrderOverrides?: boolean;
};

/**
 * Base interface for sortable filter items
 */
export type FilterItemBase = {
  count: number;
  isRefined: boolean;
  label: string;
  value: string;
};

/**
 * Custom filter item ordering for specific filter attributes
 */
export const getSearchFilterOptionOrderOverrides = (attribute: string): string[] | undefined => {
  switch (attribute) {
    case 'productDifficultyLevel':
      return OPTIMIZED_DIFFICULTY_LEVEL_ORDER;
    case 'productDurationEnum':
    case 'productDuration':
      return OPTIMIZED_DURATION_ORDER;
    case 'entityTypeDescription':
      return SEARCH_FILTER_LEARNING_PRODUCT_ORDER;
    case 'productTypeDescription':
      return isInFilterTestVariant()
        ? SEARCH_FILTER_LEARNING_PRODUCT_ORDER_EXPERIMENTAL
        : SEARCH_FILTER_LEARNING_PRODUCT_ORDER;
    case 'cost':
      return SEARCH_FILTER_COST_ORDER;
    default:
      return undefined;
  }
};

/**
 * Core filter item sorting function (non-React)
 */
export function sortFilterItems<T extends FilterItemBase>(
  items: T[],
  attribute: string,
  config: FilterSortConfig = {}
): T[] {
  const {
    sortByCount = false,
    removeFilterItems = DEFAULT_REMOVE_ITEMS,
    enrichWithSupportText = false,
    disableCustomOrderOverrides = false,
  } = config;

  // Step 1: Apply filtering to remove unwanted items
  let filteredItems = items;
  if (removeFilterItems.length > 0) {
    filteredItems = items.filter(
      (item) => !removeFilterItems.includes(item.label) && !removeFilterItems.includes(item.value)
    );
  }

  // Step 2: Enrich with support text metadata if requested
  let enrichedItems = filteredItems;
  if (enrichWithSupportText) {
    enrichedItems = getFilterItems(filteredItems, attribute) as T[];
  }

  // Step 3: Apply default sorting first
  const sortedItems = [...enrichedItems];
  sortedItems.sort((item1, item2) => {
    // Refined items always come first
    if (item1.isRefined && !item2.isRefined) return -1;
    if (!item1.isRefined && item2.isRefined) return 1;

    // Then by count or alphabetical (default sorting)
    if (sortByCount) {
      return item2.count - item1.count;
    } else {
      return item1.label.toLowerCase().localeCompare(item2.label.toLowerCase());
    }
  });

  // Step 4: Apply custom order override if available and not disabled
  const orderOverride = !disableCustomOrderOverrides ? getSearchFilterOptionOrderOverrides(attribute) : undefined;

  if (orderOverride) {
    // Apply custom ordering as secondary sort, preserving refined-first logic
    sortedItems.sort((item1, item2) => {
      // Always preserve refined items first
      if (item1.isRefined && !item2.isRefined) return -1;
      if (!item1.isRefined && item2.isRefined) return 1;

      // Within the same refined group, apply custom order
      const index1 = orderOverride.indexOf(item1.value);
      const index2 = orderOverride.indexOf(item2.value);

      // If both items are in the custom order, sort by their positions
      if (index1 !== -1 && index2 !== -1) {
        return index1 - index2;
      }
      // If only one item is in custom order, it comes first
      if (index1 !== -1) return -1;
      if (index2 !== -1) return 1;

      // If neither is in custom order, preserve existing order (from step 3)
      return 0;
    });
  }

  return sortedItems;
}

/**
 * React hook version with memoization optimizations
 * Replaces useSortedItems with consistent API
 */
export function useSortedFilterItems<T extends FilterItemBase>(
  items: T[],
  attribute: string,
  config: FilterSortConfig = {}
): T[] {
  // Memoize the filtered items to avoid unnecessary recalculations
  const filteredItems = useMemo(() => {
    const { removeFilterItems = DEFAULT_REMOVE_ITEMS } = config;
    if (removeFilterItems.length === 0) return items;
    return items.filter((item) => !removeFilterItems.includes(item.label) && !removeFilterItems.includes(item.value));
  }, [items, config]);

  // Memoize the enriched items (with support text metadata)
  const enrichedItems = useMemo(() => {
    if (config.enrichWithSupportText === false) return filteredItems;
    return getFilterItems(filteredItems, attribute) as T[];
  }, [filteredItems, attribute, config]);

  // Memoize the final sorted result
  const sortedItems = useMemo(() => {
    return sortFilterItems(enrichedItems, attribute, {
      ...config,
      // Skip filtering and enrichment since we already did them
      removeFilterItems: [],
      enrichWithSupportText: false,
    });
  }, [enrichedItems, attribute, config]);

  return sortedItems;
}
